import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { DynamicComponentExitReason } from '@jack-henry/frontend-utils/lit/dynamic-component-registry';
import { WireModelDto } from '@treasury/api/channel';
import { ApiErrorDto, isApiErrorDto } from '@treasury/api/shared';
import { NavigationService } from '@treasury/core/navigation';
import { ApprovalDecision } from '@treasury/domain/approvals/types';
import {
    DashboardService,
    PaymentType,
} from '@treasury/domain/channel/services/dashboard/dashboard-service';
import { UsersService } from '@treasury/domain/users';
import { Wire, WireBeneficiary, WireIsoService, WiresService } from '@treasury/domain/wires';
// eslint-disable-next-line no-duplicate-imports
import type { WireIsoLabels, WiresFilter } from '@treasury/domain/wires';
import { NotificationService, TmBaseComponent } from '@treasury/presentation';
import '@treasury/presentation/components/tm-blocking-loader';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-bottom-sheet';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-footer';
import { exists } from '@treasury/utils';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { showApproveRejectPrompt } from '../../pending-approvals/partials';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import '../partials/wire-data-readonly';

interface ErrorWithWire extends ApiErrorDto {
    dto: WireModelDto;
}

function isErrorWithWire(obj: unknown): obj is ErrorWithWire {
    return isApiErrorDto(obj) && exists((obj as ErrorWithWire).dto);
}

export const tagName = 'wire-container';
@customElement(tagName)
export class WireContainer extends TmBaseComponent {
    @property({ type: Number })
    public wireId = 0;

    @property({ type: Boolean, reflect: true })
    public hideHeader = false;

    @property({ type: Boolean, reflect: true })
    public useNewFlow = false;

    @property({ type: Boolean, reflect: true, attribute: 'is-approving' })
    public isApproving = false;

    @property({ type: Object })
    private currentFilter?: WiresFilter;

    @InjectProperty()
    declare private readonly headerService: HeaderBarService;

    @InjectProperty()
    declare private readonly dashboardService: DashboardService;

    @InjectProperty()
    declare private notificationService: NotificationService;

    @InjectProperty()
    declare private readonly navService: NavigationService;

    @InjectProperty()
    declare private readonly usersService: UsersService;

    @InjectProperty()
    declare private readonly wireIsoService: WireIsoService;

    @InjectProperty()
    declare private readonly wiresService: WiresService;

    @state()
    private wire = new Wire();

    @state()
    private loading = true;

    @state()
    private viewAudit = false;

    @state()
    private creditorSheetOpen = false;

    @state()
    private wireBeneficiary = {} as WireBeneficiary;

    @state()
    private wireIsoLabels: WireIsoLabels = WireIsoService.legacyLabels;

    @state()
    private completedApprovals: { dateApproved: string; name: string }[] = [];

    @state()
    private availableApprovers: string[] = [];

    @state()
    private loadingApprovals = false;

    public async firstUpdated() {
        if (!this.wireId) {
            const { params } = await this.navService.getRouteData<{
                id: string;
                filter: WiresFilter;
            }>();
            this.wireId = +params.id;
            this.currentFilter = params.filter;
            this.configureHeader();
        }
        this.wireIsoLabels = await this.wireIsoService.getLabels();
    }

    public render() {
        return [
            this.renderBlockingLoader(),
            this.renderBeneficiarySheet(),
            html`<tm-body .background=${false} .padding=${false}
                >${this.isApproving ? this.renderApprovalInformation() : nothing}
                ${this.renderDetails()}${this.isApproving
                    ? nothing
                    : this.renderApprovalInformation()}</tm-body
            >`,
            this.renderButtonBar(),
        ];
    }

    protected updated(_changedProperties: Map<PropertyKey, unknown>) {
        if (_changedProperties.has('wireId') && this.wireId) {
            this.refreshData();
        }
    }

    private async refreshData() {
        this.loading = true;
        try {
            this.wire = await this.wiresService.getWirePaymentDetails(this.wireId);
            this.configureHeader();
        } catch (error) {
            // populate wire from error object if wire returns with an errorSummary
            if (isErrorWithWire(error)) {
                this.wire = new Wire(error.dto);
            }
        } finally {
            if (this.wire.beneficiary) {
                this.wireBeneficiary = new WireBeneficiary(this.wire.beneficiary);
            }
            this.loading = false;
        }
    }

    private configureHeader() {
        if (this.hideHeader) {
            return;
        }
        const tab = this.wire.frequency?.type !== 'OneTime' ? 'Recurring' : 'OneTime';
        this.headerService.configure({
            backAction: () =>
                this.navService.navigate(`/wires`, { tab, filter: this.currentFilter }),
            title: 'Wire Details',
        });
    }

    private async approveOrReject({
        items,
        paymentType = 'wire',
    }: {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        items: any[];
        paymentType: PaymentType;
    }) {
        try {
            this.loading = true;
            await this.dashboardService.approveOrRejectPayment(items, paymentType);
            const approvedOrRejectedText =
                items[0].selectedState === 'Approved' ? 'approved' : 'rejected';
            this.notificationService.renderSuccess(
                `Payment ${approvedOrRejectedText} successfully.`
            );
            this.dispatchEvent(
                new CustomEvent('approve-or-reject', {
                    bubbles: true,
                    composed: true,
                    detail: { items, paymentType },
                })
            );
            await this.refreshData();
        } finally {
            this.loading = false;
        }
    }

    private async getApprovalInformation() {
        if (this.wire?.status === 'Pending Approval') {
            this.loadingApprovals = true;
            const { completedApprovals, availableApprovers } =
                await this.usersService.fetchAvailableAndCompletedApprovers({
                    approvalEntity: 'wire',
                    productId: this.wire.id as number | undefined,
                    updatedBy: parseInt(this.wire.updatedBy),
                });
            this.loadingApprovals = false;
            this.completedApprovals = completedApprovals;
            this.availableApprovers = availableApprovers;
        }
    }

    private renderBlockingLoader() {
        if (!this.loading) {
            return nothing;
        }
        return html`<tm-blocking-loader></tm-blocking-loader>`;
    }

    private renderBeneficiarySheet() {
        return html`<tm-bottom-sheet
            .open=${this.creditorSheetOpen}
            @close=${() => {
                this.creditorSheetOpen = false;
            }}
        >
            <p slot="header-center">${this.wireIsoLabels.creditor} Detail</p>
            <tm-body .padding=${false}>
                <creditor-data-readonly .creditor=${this.wireBeneficiary}></creditor-data-readonly>
            </tm-body>
        </tm-bottom-sheet>`;
    }

    private renderDetails() {
        if (this.loading) {
            return nothing;
        }

        return html` <wire-data-readonly
            class="bg-primary"
            .wire=${this.wire}
            .paymentDetail=${true}
            @view-creditor-detail=${() => {
                this.creditorSheetOpen = true;
            }}
            @wireFile=${() => {
                const tab = 'WireFile';
                const filter = {
                    fileName: this.wire.fileName,
                    uploadDateStart: format(this.wire.createdDate ?? new Date(), 'MM/dd/yyyy'),
                    uploadDateEnd: format(this.wire.createdDate ?? new Date(), 'MM/dd/yyyy'),
                    uploadDateType: 'dateRange',
                };
                this.navService.navigate(`/wires`, { tab, filter });
            }}
        ></wire-data-readonly>`;
    }

    private renderApprovalInformation() {
        if (this.wire?.status !== 'Pending Approval') {
            return nothing;
        }
        return html`<tm-section
            class=${classMap({
                'mt-3': !this.isApproving,
                'mb-3': this.isApproving,
            })}
            ><treasury-pending-approval-template
                .approvalItem=${this.wire}
                class="pb-2"
                .approvals=${this.wire.completedApprovals}
                .eligibleApprovers=${this.wire.availableApprovers}
            ></treasury-pending-approval-template
        ></tm-section>`;
    }

    private async showApproveReject(action: 'approve' | 'reject') {
        const paymentType = 'wire';
        const { reason, data: selectedState } = await showApproveRejectPrompt(paymentType, action);
        const vm = {
            ...this.wire.toDto(),
            selectedState,
        };

        if (reason === DynamicComponentExitReason.Confirm) {
            this.approveOrReject({
                items: [vm],
                paymentType,
            });
        }
    }

    private renderButtonBar() {
        if (this.wire?.status !== 'Pending Approval') {
            return nothing;
        }
        const canApprove = this.wire?.permissions?.some(
            (p: { permission: string }) => p.permission === 'Approve'
        );
        const canReject = this.wire?.permissions?.some(
            (p: { permission: string }) => p.permission === 'Reject'
        );

        const rejectButton = canReject
            ? {
                  error: true,
                  text: 'Reject',
                  classes: 'w-full m-1',
                  onClick: () => {
                      if (this.useNewFlow) {
                          this.dispatchEvent(
                              new CustomEvent('initiate-decision', {
                                  detail: ApprovalDecision.Reject,
                              })
                          );
                          return;
                      }

                      this.showApproveReject('reject');
                  },
              }
            : undefined;
        const approveButton = canApprove
            ? {
                  success: true,
                  text: 'Approve',
                  classes: 'w-full m-1',
                  onClick: () => {
                      if (this.useNewFlow) {
                          this.dispatchEvent(
                              new CustomEvent('initiate-decision', {
                                  detail: ApprovalDecision.Approve,
                              })
                          );
                          return;
                      }

                      this.showApproveReject('approve');
                  },
              }
            : undefined;
        const buttonConfig = [rejectButton, approveButton].filter(b => b);

        if (!canApprove && !canReject) {
            return nothing;
        }
        return html`<tm-footer direction="row" .buttonConfig=${buttonConfig}> </tm-footer>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: WireContainer;
    }
}
